import { ActionIcon, Box, Button, Text } from "@mantine/core"
import { XIcon } from "@phosphor-icons/react"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import classes from "./bulk-action-bar.module.css"
import { ReasonTooltip } from "./reason-tooltip"

export type BulkAction = {
  key: string
  label: string
  onClick: () => void
  icon?: ReactNode
  loading?: boolean
  /** Why the action cannot run on this selection (permission, status). Shown on hover; the button stays focusable. */
  reason?: ReactNode
  /** The brand-filled action; at most one. */
  primary?: boolean
}

type Props = Readonly<{
  selectedCount: number
  /** Overrides the default «N сонгосон» text. */
  label?: ReactNode
  actions: BulkAction[]
  onClear: () => void
}>

/**
 * Dark bar floating at the bottom of the page while list rows are selected (as in de-invoice-admin).
 * Carries the same actions the row offers, applied to every selected row.
 */
export function BulkActionBar({ selectedCount, label, actions, onClear }: Props) {
  const { t } = useTranslation("common")
  if (selectedCount <= 0) return null

  const summary = label ?? t("list.bulkSelected", { count: selectedCount })

  return (
    <Box className={classes.bar} role="toolbar" aria-label={typeof summary === "string" ? summary : undefined}>
      <Text className={classes.text}>{summary}</Text>
      <Box className={classes.divider} aria-hidden />
      <Box className={classes.actions}>
        {actions.map((a) => (
          <ReasonTooltip key={a.key} reason={a.reason}>
            <Button
              className={classes.btn}
              variant={a.primary ? "filled" : "default"}
              data-primary={a.primary || undefined}
              leftSection={a.icon}
              loading={a.loading}
              onClick={a.onClick}
            >
              {a.label}
            </Button>
          </ReasonTooltip>
        ))}
        <ActionIcon className={classes.close} variant="transparent" onClick={onClear} aria-label={t("list.clearSelection")}>
          <XIcon size={16} weight="bold" />
        </ActionIcon>
      </Box>
    </Box>
  )
}
